import React from 'react';

/**
 * ActionButton Component
 * 
 * Reusable button component with consistent styling used across
 * Course Overview, Lessons, and Prompts pages.
 * 
 * Features:
 * - Primary action button with #0A4872 background color
 * - Supports enabled/disabled states
 * - Optional icon/arrow
 * - Responsive sizing
 * - Hover effects: arrow moves right and button expands
 * - Variant support: 'primary' (default blue), 'completed' (green)
 */
const ActionButton = ({ 
  children, 
  onClick, 
  disabled = false, 
  className = '',
  showArrow = false,
  fullWidth = false,
  size = 'md', // 'sm', 'md', 'lg'
  variant = 'primary' // 'primary' (blue), 'completed' (green)
}) => {
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-3 text-lg'
  };

  /* focus-visible:ring provides a high-contrast focus indicator for keyboard
     navigation (WCAG 2.4.7 / 2.4.11) without affecting mouse users */
  const baseClasses = `${sizeClasses[size]} rounded-full font-semibold flex items-center ${
    fullWidth ? 'justify-center w-full' : 'justify-center'
  } space-x-2 transition-all duration-300 relative group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 ${className}`;

  const enabledClasses = 'text-white border-transparent';
  const disabledClasses = 'bg-gray-600 text-gray-400 cursor-not-allowed border-gray-600';

  // Determine background color based on variant
  const getBackgroundColor = () => {
    if (disabled) return null;
    if (variant === 'completed') return '#10b981'; // green-500
    return '#0A4872'; // brand blue (primary)
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${disabled ? disabledClasses : enabledClasses} ${
        !disabled && !fullWidth && showArrow ? 'group-hover:pr-10 md:group-hover:pr-12 lg:group-hover:pr-14' : ''
      }`}
      style={!disabled ? { backgroundColor: getBackgroundColor() } : {}}
      onMouseEnter={(e) => {
        if (!disabled && variant === 'completed') {
          e.currentTarget.style.backgroundColor = '#059669'; // green-600
        } else if (!disabled && variant === 'primary') {
          e.currentTarget.style.backgroundColor = '#083d5f'; // darker blue
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = getBackgroundColor();
        }
      }}
    >
      <span className="transition-transform duration-300 group-hover:-translate-x-0.5">{children}</span>
      {showArrow && !disabled && (
        <svg 
          aria-hidden="true"
          className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 flex-shrink-0" 
          fill="currentColor" 
          viewBox="0 0 20 20"
        >
          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      )}
    </button>
  );
};

export default ActionButton;

